/**
 * Gift-card variant picker: the design chooser and the amount cells.
 *
 * Was `blocks/product-gift-card-options.liquid`'s {% javascript %} tag. It shipped in
 * `block-scripts.js` on every page of the store to serve a single template
 * (product.gift-card). Loaded from the block now.
 */
(function () {
  if (window.__giftCardOptionsInit) return;
  window.__giftCardOptionsInit = true;

  function init(root) {
    if (root.__gcInit) return;
    root.__gcInit = true;

    var source = root.querySelector('[data-gift-card-variants]');
    if (!source) return;

    var variants;
    try {
      variants = JSON.parse(source.textContent);
    } catch (e) {
      return;
    }
    if (!variants || !variants.length) return;

    var designInputs = Array.prototype.slice.call(root.querySelectorAll('[data-gc-design]'));
    var amountInputs = Array.prototype.slice.call(root.querySelectorAll('input[name="id"]'));
    var form = root.closest('form');

    function currentDesign() {
      var checked = root.querySelector('[data-gc-design]:checked');
      return checked ? checked.value : null;
    }

    function find(design, amount) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v.amount !== amount) continue;
        // No design option on this product: amount alone identifies it.
        if (design === null || v.design === null || v.design === design) return v;
      }
      return null;
    }

    function emit(id) {
      if (!id) return;
      (form || document).dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { id: String(id) }
      }));
    }

    // The blocks' JS is concatenated into one bundle and this block sorts
    // before product-price, so an event fired during init lands before that
    // listener exists — the price then keeps the product's lowest while a
    // different amount shows as selected.
    //
    // `readyState` can't gate this: it already reads "interactive" while the
    // deferred bundle is still executing, so an early-out on it emits into the
    // void. Both paths below are guaranteed to run after the whole bundle has
    // executed, whichever fires first wins, and the flag keeps it to one
    // event. User interaction is always later than load, so it emits directly.
    var restoreEmitted = false;
    function emitWhenReady(id) {
      if (!id) return;
      function fire() {
        if (restoreEmitted) return;
        restoreEmitted = true;
        emit(id);
      }
      document.addEventListener('DOMContentLoaded', fire, { once: true });
      setTimeout(fire, 0);
    }

    // Only a real selection goes in the URL: a design chosen on its own must
    // not come back from a refresh looking like a chosen amount.
    function updateUrl(id) {
      if (!id || !window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', String(id));
        window.history.replaceState({}, '', url);
      } catch (e) { /* noop */ }
    }

    // Point every amount cell at the variant for (current design, its amount).
    // Combinations that don't exist hide their cell rather than submitting a
    // dead id.
    function retarget() {
      var design = currentDesign();
      amountInputs.forEach(function (input) {
        var cell = input.closest('[data-gc-amount-cell]');
        var match = find(design, input.getAttribute('data-gc-amount'));
        if (!match) {
          input.checked = false;
          input.disabled = true;
          if (cell) cell.hidden = true;
          return;
        }
        input.disabled = false;
        if (cell) cell.hidden = false;
        input.value = String(match.id);
        input.setAttribute('data-variant-available', match.available ? 'true' : 'false');
      });
    }

    // Any variant of this design, used to point the page at the artwork
    // before an amount has been picked. Walks the cells rather than taking
    // the first, so a design that doesn't offer the cheapest amount still
    // resolves to something.
    function representativeFor(design) {
      for (var i = 0; i < amountInputs.length; i++) {
        var match = find(design, amountInputs[i].getAttribute('data-gc-amount'));
        if (match) return match;
      }
      return null;
    }

    designInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        retarget();
        var checked = root.querySelector('input[name="id"]:checked');
        if (checked && checked.value) {
          updateUrl(checked.value);
          emit(checked.value);
          return;
        }
        // Nothing bought yet: still tell the page which artwork is on screen
        // so the gallery moves to it. No URL change — this isn't a selection.
        var representative = representativeFor(input.value);
        if (representative) emit(representative.id);
      });
    });

    amountInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        updateUrl(input.value);
        emit(input.value);
      });
    });

    // A shared/refreshed ?variant=… link restores both halves of the pair.
    function restoreFromUrl() {
      var id;
      try {
        id = new URLSearchParams(window.location.search).get('variant');
      } catch (e) {
        return false;
      }
      if (!id) return false;

      var target = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(id)) { target = variants[i]; break; }
      }
      if (!target) return false;

      if (target.design !== null) {
        designInputs.forEach(function (input) {
          if (input.value === target.design) input.checked = true;
        });
      }
      retarget();

      var amountInput = null;
      amountInputs.forEach(function (input) {
        if (input.getAttribute('data-gc-amount') === target.amount) amountInput = input;
      });
      if (!amountInput) return false;
      // Checked synchronously so the cell is already marked on first paint;
      // only the announcement waits for the other blocks to be listening.
      amountInput.checked = true;
      emitWhenReady(amountInput.value);
      return true;
    }

    if (!restoreFromUrl()) {
      // Open on the first design so the shopper always sees one marked, but
      // leave the amount unchosen — same as the size grid, so the buy button
      // asks for a selection instead of assuming the cheapest card.
      if (designInputs.length && !currentDesign()) designInputs[0].checked = true;
      retarget();
    }
  }

  function initAll() {
    document.querySelectorAll('[data-gift-card-options]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', initAll);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
